// Structured data (JSON-LD) helpers for SEO

const BASE_URL = "https://leuchtende-hotelfotografie.com";

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${BASE_URL}/#organization`,
  "name": "Leuchtende Hotelfotografie",
  "alternateName": "Agentur Haberland",
  "url": BASE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": `${BASE_URL}/favicon.png`,
    "width": 512,
    "height": 512
  },
  "image": "https://imagedelivery.net/zrm-9G6VghWX2PxlkBIQwA/3e46c252-afa9-4a67-e243-fa4a87f59100/w=1200,h=630,fit=cover,q=80,f=auto",
  "description": "Professionelle Hotelfotografie von Thomas Haberland. Leuchtende Bilder für Hotels, Resorts und Architektur.",
  "foundingDate": "2006",
  "founder": {
    "@type": "Person",
    "@id": `${BASE_URL}/#person`,
    "name": "Thomas Haberland"
  },
  "address": [
    {
      "@type": "PostalAddress",
      "streetAddress": "Lauterbachstraße 19 1/2",
      "postalCode": "84307",
      "addressLocality": "Eggenfelden",
      "addressRegion": "Bavaria",
      "addressCountry": {
        "@type": "Country",
        "name": "DE"
      }
    },
    {
      "@type": "PostalAddress",
      "addressLocality": "Berlin",
      "addressCountry": {
        "@type": "Country",
        "name": "DE"
      }
    }
  ],
  "areaServed": {
    "@type": "Place",
    "name": "Worldwide"
  },
  "telephone": "+49-171-6501432",
  "email": "info@leuchtende-hotelfotografie.de",
  "priceRange": "€€€",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "18:00"
  },
  "sameAs": []
};

export const personSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${BASE_URL}/#person`,
  "name": "Thomas Haberland",
  "jobTitle": "Hotel- und Architekturfotograf",
  "url": `${BASE_URL}/about`,
  "image": {
    "@type": "ImageObject",
    "url": `${BASE_URL}/assets/thomas-haberland.jpg`,
    "width": 800,
    "height": 1000
  },
  "description": "Über 30 Jahre Erfahrung in der professionellen Hotelfotografie. Leuchtende Technik, präzise Retusche, starke Bildsprache.",
  "knowsAbout": [
    "Hotel Photography",
    "Architectural Photography",
    "Interior Photography",
    "Food Photography",
    "Aerial Photography",
    "Photo Retouching"
  ],
  "worksFor": {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`
  }
};

export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${BASE_URL}/#service`,
  "name": "Professionelle Hotelfotografie",
  "provider": {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`
  },
  "serviceType": "Hotel & Resort Photography",
  "areaServed": "Europe",
  "description": "Komplette Fotoproduktion: Hotel & Resort, Architektur & Interieur, Gastronomie, Luftaufnahmen, Retusche & Visual Branding.",
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Leistungen",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Hotel- & Resortfotografie",
          "description": "Professionelle Fotografie für Hotels und Resorts aller Kategorien"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Architektur & Interieur",
          "description": "Architektur- und Innenraumfotografie für Hotels und Gebäude"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Gastronomie & Lifestyle",
          "description": "Food-Fotografie und Lifestyle-Aufnahmen"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Luftaufnahmen & Motion",
          "description": "Drohnenaufnahmen und Videoproduktion"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Retusche & Visual Branding",
          "description": "Professionelle Bildbearbeitung und visuelles Branding"
        }
      }
    ]
  }
};

export const createWebPageSchema = (
  name: string,
  description: string,
  url: string
) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": url,
  "name": name,
  "description": description,
  "url": url,
  "inLanguage": "de-DE",
  "isPartOf": {
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`,
    "name": "leuchtende hotelfotografie",
    "url": BASE_URL
  },
  "publisher": {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`
  }
});

export const createBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const createFAQSchema = (questions: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": questions.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Comprehensive Photograph schema for portfolio images
export const createPhotographSchema = (image: {
  url: string;
  thumbnailUrl?: string;
  title: string;
  description?: string;
  location?: string;
  stars?: string;
  category?: string;
  width?: number;
  height?: number;
  dateCreated?: string;
}) => ({
  "@type": "Photograph",
  "url": image.url,
  "contentUrl": image.url,
  "thumbnailUrl": image.thumbnailUrl || image.url,
  "name": image.title,
  "headline": `${image.title}${image.location ? ` - ${image.location}` : ''}`,
  "description": image.description || `Professionelle Hotelfotografie: ${image.title}${image.location ? ` in ${image.location}` : ''}${image.stars ? ` (${image.stars} Sterne)` : ''}`,
  "caption": `Leuchtende Hotelfotografie: ${image.title}`,
  "representativeOfPage": false,
  "width": image.width ? { "@type": "QuantitativeValue", "value": image.width, "unitCode": "E37" } : undefined,
  "height": image.height ? { "@type": "QuantitativeValue", "value": image.height, "unitCode": "E37" } : undefined,
  "contentLocation": image.location ? {
    "@type": "Place",
    "name": image.location,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": image.location
    }
  } : undefined,
  "genre": image.category || "Hotel Photography",
  "creator": {
    "@type": "Person",
    "@id": `${BASE_URL}/#person`,
    "name": "Thomas Haberland",
    "jobTitle": "Hotel- und Architekturfotograf",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Lauterbachstraße 19 1/2",
      "postalCode": "84307",
      "addressLocality": "Eggenfelden",
      "addressRegion": "Bavaria",
      "addressCountry": "DE"
    },
    "telephone": "+49-171-6501432"
  },
  "author": {
    "@type": "Person",
    "@id": `${BASE_URL}/#person`
  },
  "copyrightHolder": {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    "name": "Leuchtende Hotelfotografie"
  },
  "copyrightYear": new Date().getFullYear(),
  "copyrightNotice": `© ${new Date().getFullYear()} Thomas Haberland / Leuchtende Hotelfotografie`,
  "creditText": "Thomas Haberland / Leuchtende Hotelfotografie",
  "license": "https://creativecommons.org/licenses/by-nc-nd/4.0/",
  "acquireLicensePage": `${BASE_URL}/contact`,
  "accessibilityFeature": ["alternativeText", "highContrastDisplay"],
  "inLanguage": "de-DE",
  "dateCreated": image.dateCreated || undefined,
  "encodingFormat": "image/jpeg"
});

// Portfolio/Gallery page schema with comprehensive ImageGallery + MediaGallery
export const createImageGallerySchema = (images: Array<{
  url: string;
  thumbnailUrl?: string;
  title: string;
  description?: string;
  location?: string;
  stars?: string;
  category?: string;
  width?: number;
  height?: number;
}>) => ({
  "@context": "https://schema.org",
  "@type": "ImageGallery",
  "@id": `${BASE_URL}/portfolio#gallery`,
  "name": "Portfolio – Leuchtende Hotelfotografie",
  "headline": "Leuchtende Hotelbilder von Thomas Haberland",
  "description": "Leuchtende Hotelbilder aus über 30 Jahren Erfahrung. 5-Sterne-Resorts, Boutique-Hotels, Architektur, Interior, Food und Luftaufnahmen.",
  "url": `${BASE_URL}/portfolio`,
  "inLanguage": "de-DE",
  "accessibilityFeature": ["alternativeText", "highContrastDisplay", "structureNavigation"],
  "author": {
    "@type": "Person",
    "@id": `${BASE_URL}/#person`,
    "name": "Thomas Haberland"
  },
  "creator": {
    "@type": "Person",
    "@id": `${BASE_URL}/#person`,
    "name": "Thomas Haberland",
    "jobTitle": "Hotel- und Architekturfotograf"
  },
  "copyrightHolder": {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`
  },
  "copyrightYear": new Date().getFullYear(),
  "license": "https://creativecommons.org/licenses/by-nc-nd/4.0/",
  "about": {
    "@type": "Thing",
    "name": "Professional Hotel Photography",
    "description": "Leuchtende Hotelfotografie für Hotels, Resorts und Architektur"
  },
  "genre": ["Hotel Photography", "Architectural Photography", "Interior Photography", "Food Photography", "Aerial Photography"],
  "numberOfItems": images.length,
  "primaryImageOfPage": images.length > 0 ? {
    "@type": "ImageObject",
    "url": images[0].url,
    "name": images[0].title,
    "caption": `Leuchtende Hotelfotografie: ${images[0].title}`
  } : undefined,
  "hasPart": images.slice(0, 50).map((img, index) => ({
    ...createPhotographSchema(img),
    "position": index + 1,
    "isPartOf": {
      "@type": "ImageGallery",
      "@id": `${BASE_URL}/portfolio#gallery`
    }
  })),
  "mainEntity": {
    "@type": "ItemList",
    "name": "Portfolio Images",
    "numberOfItems": images.length,
    "itemListElement": images.slice(0, 50).map((img, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Photograph",
        "url": img.url,
        "name": img.title,
        "description": img.description || `Hotelfotografie: ${img.title}`,
        "contentLocation": img.location ? { "@type": "Place", "name": img.location } : undefined
      }
    }))
  },
  "isPartOf": {
    "@type": "WebSite",
    "@id": `${BASE_URL}/#website`
  },
  "publisher": {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`
  }
});

// About/Profile page schema
export const createProfilePageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ProfilePage",
  "mainEntity": personSchema,
  "name": "Thomas Haberland – Leuchtende Hotelfotografie",
  "description": "Über 30 Jahre Erfahrung. Leuchtende Technik, präzise Retusche, starke Bildsprache für Hotels, Resorts und Architektur.",
  "url": `${BASE_URL}/about`,
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": "Über uns", "item": `${BASE_URL}/about` }
    ]
  }
});

// Services page schema
export const createServicesPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Leistungen – Hotelfotografie, Interieur, Luftaufnahmen, Retusche",
  "description": "Komplette Fotoproduktion: Hotel & Resort, Architektur & Interieur, Gastronomie, Luftaufnahmen, Retusche & Visual Branding.",
  "url": `${BASE_URL}/services`,
  "mainEntity": serviceSchema,
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": "Leistungen", "item": `${BASE_URL}/services` }
    ]
  }
});

// Contact page schema
export const createContactPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Kontakt & Beratung – Leuchtende Hotelfotografie",
  "description": "Jetzt Erstgespräch buchen: Wie leuchtende Hotelfotografie Sichtbarkeit, ADR und Direktbuchungen steigert.",
  "url": `${BASE_URL}/contact`,
  "mainEntity": {
    "@type": "LocalBusiness",
    "@id": `${BASE_URL}/#localbusiness`,
    "name": "leuchtende hotelfotografie",
    "image": `${BASE_URL}/favicon.png`,
    "telephone": "+49-171-6501432",
    "email": "info@leuchtende-hotelfotografie.de",
    "url": BASE_URL,
    "priceRange": "€€€",
    "address": [
      {
        "@type": "PostalAddress",
        "streetAddress": "Lauterbachstraße 19 1/2",
        "postalCode": "84307",
        "addressLocality": "Eggenfelden",
        "addressRegion": "Bavaria",
        "addressCountry": {
          "@type": "Country",
          "name": "DE"
        }
      },
      {
        "@type": "PostalAddress",
        "addressLocality": "Berlin",
        "addressCountry": {
          "@type": "Country",
          "name": "DE"
        }
      }
    ],
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 48.4031,
      "longitude": 12.7631
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    },
    "sameAs": []
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
      { "@type": "ListItem", "position": 2, "name": "Kontakt", "item": `${BASE_URL}/contact` }
    ]
  }
});

// Single image schema for lightbox
export const createImageObjectSchema = (image: {
  url: string;
  title: string;
  location?: string;
  stars?: string;
  category?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "ImageObject",
  "url": image.url,
  "name": image.title,
  "description": `Hotelfotografie: ${image.title}${image.location ? ` in ${image.location}` : ''}${image.stars ? ` (${image.stars} Sterne)` : ''}`,
  "contentLocation": image.location ? {
    "@type": "Place",
    "name": image.location
  } : undefined,
  "author": {
    "@type": "Person",
    "@id": `${BASE_URL}/#person`
  },
  "copyrightHolder": {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`
  },
  "copyrightYear": new Date().getFullYear(),
  "license": "https://creativecommons.org/licenses/by-nc-nd/4.0/"
});

export const createSiteSchema = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      ...organizationSchema,
      "@context": undefined
    },
    {
      ...personSchema,
      "@context": undefined
    },
    {
      ...serviceSchema,
      "@context": undefined
    },
    {
      "@type": "WebSite",
      "@id": `${BASE_URL}/#website`,
      "name": "leuchtende hotelfotografie",
      "url": BASE_URL,
      "description": "Professionelle Hotelfotografie von Thomas Haberland",
      "publisher": {
        "@type": "Organization",
        "@id": `${BASE_URL}/#organization`
      },
      "inLanguage": "de-DE"
    }
  ]
});